import { isValidElement, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { slugify } from "../docs";

/**
 * Customer documentation, rendered from the repo's markdown at build time.
 * GitHub-flavored (tables, task lists); raw HTML is not rendered, so a doc
 * cannot carry a script. Headings get GitHub-style ids so a link written
 * by hand in one file reaches a heading in another, and "on this page" can
 * point at them. Classes are the portal's, not a typography plugin's, so
 * the docs read like the rest of the product.
 */
const textOf = (node: ReactNode): string =>
  Array.isArray(node) ? node.map(textOf).join("") : typeof node === "string" || typeof node === "number" ? String(node) : node && typeof node === "object" && "props" in node ? textOf((node as { props: { children?: ReactNode } }).props.children) : "";

const heading = (level: 2 | 3 | 4) =>
  function Heading({ children }: { children?: ReactNode }) {
    const id = slugify(textOf(children));
    const cls = level === 2 ? "mt-8 scroll-mt-[calc(var(--page-bar)+1rem)] text-xl font-semibold" : level === 3 ? "mt-6 scroll-mt-[calc(var(--page-bar)+1rem)] text-base font-semibold" : "mt-4 scroll-mt-[calc(var(--page-bar)+1rem)] text-sm font-semibold uppercase tracking-wide text-neutral-600 dark:text-neutral-400";
    const Tag = `h${level}` as const;
    return (
      <Tag id={id} tabIndex={-1} className={cls}>
        {children}
      </Tag>
    );
  };

// Made once, not per render: a new component type on every render would remount each heading and drop the focus a jump put on it (#417).
const H2 = heading(2);
const H3 = heading(3);
const H4 = heading(4);

/**
 * A fenced block (#421): its lines as written - a diagram is drawn in
 * them, so they never wrap - scrolling sideways inside its own box when
 * wider than the column. A block that scrolls takes keyboard focus and is
 * named, so a keyboard can scroll it too (axe scrollable-region-focusable);
 * one that fits is not an extra tab stop. The fence's <code> is read from
 * its props: without a language react-markdown gives it no class, which
 * once made a diagram render as inline code and spill over the rail.
 */
function CodeBlock({ children }: { children?: ReactNode }) {
  const ref = useRef<HTMLPreElement>(null);
  const [scrolls, setScrolls] = useState(false);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const measure = () => setScrolls(el.scrollWidth > el.clientWidth);
    measure();
    if (typeof ResizeObserver !== "function") return;
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  const code = isValidElement<{ className?: string; children?: ReactNode }>(children) ? children.props : { children };
  const language = /language-(\S+)/.exec(code.className ?? "")?.[1];
  return (
    <pre
      ref={ref}
      tabIndex={scrolls ? 0 : undefined}
      role={scrolls ? "region" : undefined}
      aria-label={scrolls ? `${language ? `Code (${language})` : "Code"}, scrolls sideways` : undefined}
      className="mt-3 max-w-full overflow-x-auto whitespace-pre rounded-lg bg-neutral-100 p-3 font-mono text-xs leading-5 dark:bg-neutral-800"
    >
      <code>{code.children}</code>
    </pre>
  );
}

export function Markdown({ source }: { source: string }) {
  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      components={{
        // The page title is the document's `#`; a `#` inside the body is demoted so the page keeps one h1.
        h1: H2,
        h2: H2,
        h3: H3,
        h4: H4,
        p: ({ children }) => <p className="mt-3 text-sm leading-6">{children}</p>,
        ul: ({ children }) => <ul className="mt-3 list-disc space-y-1 pl-5 text-sm leading-6">{children}</ul>,
        ol: ({ children }) => <ol className="mt-3 list-decimal space-y-1 pl-5 text-sm leading-6">{children}</ol>,
        li: ({ children }) => <li>{children}</li>,
        a: ({ href, children }) => {
          const external = Boolean(href && /^https?:/.test(href));
          return (
            <a href={href} className="text-accent-strong underline hover:text-accent dark:text-accent" {...(external ? { target: "_blank", rel: "noopener noreferrer" } : {})}>
              {children}
            </a>
          );
        },
        // Inline code only: a fenced block's <code> is rendered by CodeBlock, which reads it from its <pre>.
        code: ({ children }) => <code className="break-words rounded bg-neutral-100 px-1 py-0.5 font-mono text-[0.85em] dark:bg-neutral-800">{children}</code>,
        pre: CodeBlock,
        blockquote: ({ children }) => <blockquote className="mt-3 border-l-2 border-neutral-300 pl-3 text-sm text-neutral-600 dark:border-neutral-700 dark:text-neutral-400">{children}</blockquote>,
        table: ({ children }) => (
          <div className="mt-3 overflow-x-auto">
            <table className="w-full text-left text-sm">{children}</table>
          </div>
        ),
        thead: ({ children }) => <thead className="border-b border-neutral-200 text-xs uppercase tracking-wide text-neutral-600 dark:border-neutral-800 dark:text-neutral-400">{children}</thead>,
        th: ({ children }) => <th className="py-2 pr-4 font-medium">{children}</th>,
        td: ({ children }) => <td className="border-b border-neutral-100 py-2 pr-4 align-top leading-6 dark:border-neutral-800/60">{children}</td>,
        hr: () => <hr className="my-6 border-neutral-200 dark:border-neutral-800" />,
        strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
      }}
    >
      {source}
    </ReactMarkdown>
  );
}
